import React, { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Card, Button, Spinner, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../config/ApiConfig';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [afterSend, setAfterSend] = useState(false);
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();

  const inputRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
  ];

  const sendMail = async () => {
    if (!email) {
      setError("Veuillez entrer votre adresse email");
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post('/reset', { email });
      toast.success("Un code de réinitialisation a été envoyé à votre adresse email");
      setAfterSend(true);
      setError("");
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.errors || err.response?.data?.message || 'Email introuvable');
      toast.error(err.response?.data?.errors || 'Email introuvable');
    } finally {
      setIsLoading(false);
    }
  };

  function handleInput(e, index) {
    const input = e.target;
    const previousInput = inputRefs[index - 1];
    const nextInput = inputRefs[index + 1];

    const newCode = [...code];
    if (/^[a-z]+$/.test(input.value)) {
      const uc = input.value.toUpperCase();
      newCode[index] = uc;
      inputRefs[index].current.value = uc;
    } else {
      newCode[index] = input.value;
    }
    setCode(newCode.join(''));

    input.select();

    if (input.value === '') {
      if (previousInput) {
        previousInput.current.focus();
      }
    } else if (nextInput) {
      nextInput.current.select();
    }
  }

  function handleFocus(e) {
    e.target.select();
  }

  function handleKeyDown(e, index) {
    const input = e.target;
    const previousInput = inputRefs[index - 1];

    if ((e.keyCode === 8 || e.keyCode === 46) && input.value === '') {
      e.preventDefault();
      setCode((prevCode) => prevCode.slice(0, index) + prevCode.slice(index + 1));
      if (previousInput) {
        previousInput.current.focus();
      }
    }
  }

  const handlePaste = (e) => {
    const pastedCode = e.clipboardData.getData('text');
    if (pastedCode.length === 4) {
      setCode(pastedCode);
      inputRefs.forEach((inputRef, index) => {
        inputRef.current.value = pastedCode.charAt(index);
      });
    }
  };

  const handleConfirmCode = async () => {
    if (code.length !== 4) {
      toast.error("Veuillez entrer le code à 4 chiffres");
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.post('/verify-reset-code', { email, code });
      if (response?.data?.success) {
        toast.success("Code vérifié avec succès");
        history.push("/change-password", { email, code });
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.errors || err.response?.data?.message || "Code invalide");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="d-flex align-items-center justify-content-center min-vh-100 bg-light">
      <Card className="shadow" style={{ maxWidth: "500px", width: "100%" }}>
        <Card.Body className="p-5">
          {afterSend ? (
            <>
              <div className="text-center mb-4">
                <h2 className="mb-3">Marion Livraison</h2>
                <h4>Valide ton adresse e-mail</h4>
                <p className="text-muted">
                  Le code permettant de réinitialiser ton mot de passe a été envoyé à <strong>{email}</strong>.
                  Si tu ne le vois pas, vérifie dans tes spams.
                </p>
              </div>

              <div className="d-flex justify-content-center gap-2 mb-4">
                {[0, 1, 2, 3].map((index) => (
                  <input
                    key={index}
                    type="text"
                    maxLength={1}
                    onChange={(e) => handleInput(e, index)}
                    ref={inputRefs[index]}
                    autoFocus={index === 0}
                    onFocus={handleFocus}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onPaste={handlePaste}
                    className="form-control text-center"
                    style={{ width: "60px", height: "60px", fontSize: "24px" }}
                  />
                ))}
              </div>

              <Button
                variant="primary"
                className="w-100"
                onClick={handleConfirmCode}
                disabled={isLoading || code.length !== 4}
              >
                {isLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Vérification...
                  </>
                ) : (
                  "Confirmer"
                )}
              </Button>
            </>
          ) : (
            <>
              <div className="text-center mb-4">
                <h2 className="mb-3">Marion Livraison</h2>
                <h4>Mot de passe oublié</h4>
                <p className="text-muted">
                  Confirme ton e-mail et nous t'enverrons un code pour réinitialiser ton mot de passe.
                </p>
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Adresse e-mail</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="ton@email.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setError("");
                  }}
                  isInvalid={!!error}
                />
                {error && <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>}
              </Form.Group>

              <Button
                variant="primary"
                className="w-100 mb-3"
                onClick={sendMail}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Envoi en cours...
                  </>
                ) : (
                  "Envoyer le code"
                )}
              </Button>

              <div className="text-center">
                <a href="/login">Retour à la connexion</a>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
